"use server";

import { revalidatePath } from "next/cache";
import { headers } from "next/headers";
import { gql } from "@/lib/lecturehub/client";
import { verifySession } from "@/lib/lecturehub/dal";
import { friendlyMessage, GqlRequestError } from "@/lib/lecturehub/errors";
import { M_COMMENT_MY_ISSUE_REPORT, M_REPORT_ISSUE } from "@/lib/lecturehub/operations";
import { loadPortalConfig } from "@/lib/lecturehub/portal-config";
import { siteOrigin } from "@/lib/lecturehub/site-url";
import type { IssueReportStatusEnum, ReportIssueInput } from "@/lib/lecturehub/types";
import { getRequestDict } from "@/lib/i18n-server";
import { defaultLang, isLang, langPath, localeHeader, type Dict, type Lang } from "@/lib/i18n";
import { absoluteUrl } from "@/lib/seo";
import {
  buildContext,
  COMMENT_MAX,
  DESCRIPTION_MAX,
  fileProblem,
  safeEventId,
  safeFromPath,
  titleFromDescription,
  type FileProblem,
} from "@/lib/issues/rules";
import type { FormState } from "@/lib/actions/result";

export type IssueFormState = FormState;
/** A report answers with its id: the panel links to it, and the member never
 *  leaves the page they were reporting. */
export type ReportFormState = FormState<{ id: string }>;

function langField(formData: FormData): Lang {
  const raw = formData.get("lang");
  return typeof raw === "string" && isLang(raw) ? raw : defaultLang();
}

/** Real files only: an empty file input still posts one zero-byte part. */
function filesField(formData: FormData): File[] {
  return formData.getAll("files").filter((f): f is File => f instanceof File && f.size > 0);
}

function fileMessage(problem: FileProblem, t: Dict["issues"]): string {
  return problem === "count" ? t.fileCount : problem === "size" ? t.fileSize : t.fileType;
}

/**
 * The backend refuses a file whose bytes do not match its name with a
 * validation error on `files.N` — in English whatever X-Locale says (dev,
 * 2026-10-02). Our client check cannot see bytes, so this is the one file
 * refusal that reaches the member from upstream; it gets our own sentence.
 */
function failure(err: unknown, dict: Dict): string {
  const v = err instanceof GqlRequestError ? err.validation() : null;
  if (v && Object.keys(v).some((k) => k.startsWith("files"))) return dict.issues.fileType;
  return friendlyMessage(err, dict.errors);
}

/**
 * File a report and answer with its id: the panel shows the confirmation and
 * the member stays on the page they were reporting. The member writes one
 * thing — what happened; the title is its first line, and the category is
 * left to the backend's default (BUG) for the team to change while triaging.
 * The files ride as a GraphQL multipart request (gql's `uploads`).
 * Diagnostics are built HERE, not taken from the browser: the user agent from
 * the request, the build from this deploy; only the viewport and the last
 * Sentry event id have to come from the client.
 */
export async function reportIssueAction(
  _prev: ReportFormState,
  formData: FormData,
): Promise<ReportFormState> {
  await verifySession();
  const lang = langField(formData);
  const [dict, portal] = await Promise.all([getRequestDict(), loadPortalConfig()]);
  const t = dict.issues;
  if (!portal.modules.issueReports) return { ok: false, error: dict.errors.issueReportsDisabled };

  const description = String(formData.get("description") ?? "").trim();
  if (!description || description.length > DESCRIPTION_MAX) {
    return { ok: false, error: t.descriptionRequired };
  }

  const files = filesField(formData);
  const problem = fileProblem(files);
  if (problem) return { ok: false, error: fileMessage(problem, t) };

  const from = safeFromPath(String(formData.get("from") ?? ""));
  const input: ReportIssueInput = {
    title: titleFromDescription(description),
    description,
    pageUrl: from ? absoluteUrl(from, await siteOrigin()) : null,
    context: buildContext({
      userAgent: (await headers()).get("user-agent"),
      viewport: String(formData.get("viewport") ?? ""),
      build: process.env.NEXT_DEPLOYMENT_ID ?? null,
      sentryEventId: safeEventId(formData.get("sentryEventId")),
    }),
  };

  let id: string;
  try {
    const r = await gql<{ reportIssue: { id: string } }>(
      M_REPORT_ISSUE,
      { input },
      {
        cache: "no-store",
        // Backend validation messages (a file whose bytes do not match its
        // name) come back in the request's language — friendlyMessage shows them.
        locale: localeHeader(lang),
        uploads: files.length ? { variable: "files", files } : undefined,
      },
    );
    id = r.reportIssue.id;
  } catch (err) {
    return { ok: false, error: failure(err, dict) };
  }
  revalidatePath(langPath(lang, "/profil/prijave"));
  return { ok: true, id };
}

/**
 * A public reply on the member's own report. Not gated on the toggle: upstream
 * keeps replies working when the portal closes the door to NEW reports.
 * `already` is the report's file count, so the cap of five is checked before
 * the upload rather than after it.
 */
export async function replyIssueAction(
  _prev: IssueFormState,
  formData: FormData,
): Promise<IssueFormState & { status?: IssueReportStatusEnum }> {
  await verifySession();
  const lang = langField(formData);
  const dict = await getRequestDict();
  const t = dict.issues;

  const id = String(formData.get("id") ?? "");
  const body = String(formData.get("body") ?? "").trim();
  if (!id) return { ok: false, error: dict.errors.issueReportNotFound };
  if (!body || body.length > COMMENT_MAX) return { ok: false, error: t.replyRequired };

  const files = filesField(formData);
  const already = Number(formData.get("already") ?? 0) || 0;
  const problem = fileProblem(files, already);
  if (problem) return { ok: false, error: fileMessage(problem, t) };

  try {
    const r = await gql<{ commentMyIssueReport: { id: string; status: IssueReportStatusEnum } }>(
      M_COMMENT_MY_ISSUE_REPORT,
      { id, body },
      {
        cache: "no-store",
        // Backend validation messages (a file whose bytes do not match its
        // name) come back in the request's language — friendlyMessage shows them.
        locale: localeHeader(lang),
        uploads: files.length ? { variable: "files", files } : undefined,
      },
    );
    revalidatePath(langPath(lang, `/profil/prijave/${encodeURIComponent(id)}`));
    return { ok: true, status: r.commentMyIssueReport.status };
  } catch (err) {
    return { ok: false, error: failure(err, dict) };
  }
}
